import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Spinner } from "../ui/spinner";
import { MetadataService } from "../../services/metadataService";

const EditLikelihoodForm = ({
  open,
  onClose,
  likelihood,
  onLikelihoodUpdated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    value: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (likelihood && open) {
      setFormData({
        value: likelihood.value?.toString() || "",
        description: likelihood.description || "",
      });
      setError(null);
    }
  }, [likelihood, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!likelihood) return;

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        value: parseInt(formData.value),
        description: formData.description.trim(),
      };

      await MetadataService.updateLikelihood(
        likelihood.id,
        updateData,
        getAccessTokenSilently
      );

      onLikelihoodUpdated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isValid = formData.value && formData.description.trim();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Likelihood Level</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="value">Level Value</Label>
            <Input
              id="value"
              name="value"
              type="number"
              min="1"
              max="10"
              value={formData.value}
              onChange={handleInputChange}
              placeholder="Enter level value (1-10)"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter likelihood description (e.g., Almost Certain, Likely, Possible, Unlikely, Rare)"
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!isValid || loading}>
              {loading && <Spinner size="sm" className="mr-2" />}
              Update Likelihood
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditLikelihoodForm;
